import * as Sentry from "@Sentry";
import Text from "@CubeComponents/html/Text/Text";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@Services/axiosWrapper";
import IconComponent from "@CubeComponents/icons";
import { useState, useEffect, memo, useCallback, useRef } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { fileConversionStatus, exportType } from "../../../../helpers/constants";
import { showToastNotification } from "../../reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../../services/constants";
import { useTranslation } from "@i18n";
import type {
  DeleteExportDataResponse,
  ExportScheduleTasksResponse,
  GetAllExportDataResponse,
} from "@ServerTypes/schedule/schedule.schema";
import NoData from "@Assets/icons/svg/NoData.svg";
import { useNavigate } from "react-router-dom";
import { getAllNotificationPageUrl } from "@Services/utils/helpers";
import { setShowNotification } from "../../actions/notification";

function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

interface ExportItem {
  _id: string;
  awsKey: string | null;
  fileName?: string | undefined;
  exportType?: string | undefined;
  createdAt: number;
  exportStatus?: string | undefined;
  scheduleId?: string | undefined;
}

interface SectionHeaderProps {
  title: string;
  buttonText?: string | undefined;
}

interface SectionContentProps {
  items: ExportItem[];
  sectionType: "ongoing" | "completed";
  onDelete?: ((id: string) => void) | undefined;
  onRetry?: ((id: string, scheduleId: string, type: string) => void) | undefined;
  onDownload?: ((awsKey: string, fileName: string) => void) | undefined;
}

interface ExportSectionProps {
  title: string;
  items: ExportItem[];
  buttonText?: string | undefined;
  sectionType: "ongoing" | "completed";
  onDelete?: ((id: string) => void) | undefined;
  onRetry?: ((id: string, scheduleId: string, type: string) => void) | undefined;
  onDownload?: ((awsKey: string, fileName: string) => void) | undefined;
}

interface ExportedFilesProps {
  // ⚠️ `onCountChange` MUST be reference-stable (a `useCallback` with no changing deps). It is in
  // `fetchData`'s deps, and `fetchData` is the 30s poll effect's only dep - see `fetchData` below.
  onCountChange?: (count: number) => void;
}

function SectionHeader({ title, buttonText }: SectionHeaderProps) {
  return (
    <div className="flex h-14 items-center justify-between px-6 border-b border-[#DCE8F5] font-bold">
      <Text>{title}</Text>
      {buttonText && (
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} size="small">
          {buttonText}
        </ButtonComponent>
      )}
    </div>
  );
}

const SectionContent = memo(function SectionContent({
  items,
  sectionType,
  onDelete,
  onRetry,
  onDownload,
}: SectionContentProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      {items.map((item: ExportItem) => {
        const fileName = item.fileName || item.awsKey || "";
        const formattedDate = formatTimestamp(item.createdAt);
        const isExcel = item.exportType?.toLowerCase() === exportType.EXCEL.toLowerCase();
        const isPdf = item.exportType?.toLowerCase() === exportType.PDF.toLowerCase();
        const isP6 = item.exportType?.toLowerCase() === exportType.P6.toLowerCase();
        const isMSP = item.exportType?.toLowerCase() === exportType.MSP.toLowerCase();
        const isSimulationVideo = item.exportType?.toLowerCase() === exportType.SIMULATION_VIDEO.toLowerCase();

        return (
          <div key={item._id} className="flex items-center px-6 py-4 border-b border-[#DCE8F5] last:border-b-0 gap-3">
            {isExcel && <IconComponent name="FileXLSX" className="shrink-0" width="1.5rem" height="1.5rem" />}
            {isPdf && <IconComponent name="FilePDF" className="shrink-0" width="1.5rem" height="1.5rem" />}
            {isP6 && <IconComponent name="primaveraP6" className="shrink-0" width="1.5rem" height="1.5rem" />}
            {isMSP && <IconComponent name="msproject" className="shrink-0" width="1.5rem" height="1.5rem" />}
            {isSimulationVideo && <IconComponent name="FileMP4" className="shrink-0" width="1.5rem" height="1.5rem" />}
            {!isExcel && !isPdf && !isP6 && !isMSP && !isSimulationVideo && (
              <IconComponent name="file" className="shrink-0" width="1.5rem" height="1.5rem" />
            )}
            <div className="flex flex-col gap-1 flex-1 min-w-0">
              <Text className="truncate" title={fileName}>
                {fileName}
              </Text>
              <span
                className={`text-xs ${
                  item.exportStatus === fileConversionStatus.ERRORED ? "text-[#D92D20]" : "text-gray-500"
                }`}
              >
                {(() => {
                  if (item.exportStatus === fileConversionStatus.IN_PROGRESS) {
                    return t("Processing your export");
                  } else if (item.exportStatus === fileConversionStatus.ERRORED) {
                    return t("Processing failed");
                  } else {
                    return formattedDate;
                  }
                })()}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {sectionType === "ongoing" && (
                <>
                  {item.exportStatus === fileConversionStatus.IN_PROGRESS && (
                    <IconComponent
                      name="loader-icon"
                      className="animate-spin cursor-pointer"
                      width="1.25rem"
                      height="1.25rem"
                    />
                  )}

                  {item.exportStatus === fileConversionStatus.ERRORED && (
                    <IconComponent
                      name="reset_template"
                      className="cursor-pointer hover:text-blue-600"
                      onClick={() =>
                        item.scheduleId && item.exportType && onRetry?.(item._id, item.scheduleId, item.exportType)
                      }
                      width="1.25rem"
                      height="1.25rem"
                    />
                  )}

                  {item.exportStatus === fileConversionStatus.ERRORED && (
                    <IconComponent
                      name="delete"
                      className="cursor-pointer hover:text-red-600"
                      onClick={() => onDelete?.(item._id)}
                      width="1.25rem"
                      height="1.25rem"
                    />
                  )}
                </>
              )}
              {sectionType === "completed" && (
                <IconComponent
                  name="download"
                  className="cursor-pointer hover:text-blue-600"
                  onClick={() => item.awsKey && onDownload?.(item.awsKey, item.fileName || item.awsKey)}
                  width="1.25rem"
                  height="1.25rem"
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
});

const ExportSection = memo(function ExportSection({
  title,
  items,
  buttonText,
  sectionType,
  onDelete,
  onRetry,
  onDownload,
}: ExportSectionProps) {
  return (
    <div className="border border-[#DCE8F5] bg-white rounded-md">
      <SectionHeader title={title} buttonText={buttonText} />
      <SectionContent
        items={items}
        sectionType={sectionType}
        onDelete={onDelete}
        onRetry={onRetry}
        onDownload={onDownload}
      />
    </div>
  );
});

function EmptyExportsState() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleViewAllExports = () => {
    // Fire-and-forget: router navigation; the dispatch below does not depend on it.
    void navigate(getAllNotificationPageUrl({ section: "exportedFiles" }));
    dispatch(setShowNotification(false));
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full py-16">
      <img src={NoData} alt="No Exports" className="w-64 h-64 mb-6" />
      <Text className="text-xl font-semibold text-[#3e8ab0] mb-4">{t("No Exports Available")}</Text>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleViewAllExports}>
        {t("View all Exports")}
      </ButtonComponent>
    </div>
  );
}

function ExportedFiles({ onCountChange }: ExportedFilesProps) {
  const dispatch = useDispatch();
  const [ongoingData, setOngoingData] = useState<ExportItem[]>([]);
  const [completedData, setCompletedData] = useState<ExportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const skipRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);
  const observerRef = useRef<HTMLDivElement>(null);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const result = await axiosWrapper.post<DeleteExportDataResponse>(`${getBackendUrl()}/schedule/deleteExportData`, {
        data: {
          exportId: id,
        },
      });

      // `deleteExportData` answers 200 with `status: false` on failure (the shared error handler), so
      // the flag is what decides - the AxiosResponse object itself is always truthy.
      if (result.data.status) {
        setOngoingData((prev) => prev.filter((item: ExportItem) => item._id !== id));
        setCompletedData((prev) => prev.filter((item: ExportItem) => item._id !== id));

        dispatch(
          showToastNotification({
            content: t("Export deleted successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: t("Export deletion failed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        showToastNotification({
          content: t("An error occurred while deleting the export"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Sentry.captureException(error);
    }
  }, []);

  const handleRetry = useCallback(async (id: string, scheduleId: string, type: string) => {
    try {
      const result = await axiosWrapper.post<ExportScheduleTasksResponse>(
        `${getBackendUrl()}/schedule/exportScheduleTasks?scheduleId=${scheduleId}`,
        {
          _id: id,
          type,
        },
      );

      // Same as the delete above: a failed retry still resolves with 200 and `status: false`.
      if (result.data.status) {
        setOngoingData((prev) =>
          prev.map((item: ExportItem) =>
            item._id === id ? { ...item, exportStatus: fileConversionStatus.IN_PROGRESS } : item,
          ),
        );

        dispatch(
          showToastNotification({
            content: t("Export retry initiated successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: t("Export retry failed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        showToastNotification({
          content: t("An error occurred while retrying the export"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Sentry.captureException(error);
    }
  }, []);

  // Synchronous: this only builds a URL and clicks a temporary anchor. `onDownload` is declared
  // `(awsKey, fileName) => void` on both list components that receive it.
  const handleDownload = useCallback((awsKey: string, fileName: string) => {
    try {
      const encodedKey = encodeURIComponent(awsKey);
      const encodedName = encodeURIComponent(fileName);
      const downloadUrl = new URL(
        `${getBackendUrl()}/fileSystemServer/getMailAttachmentFile/${encodedKey}/${encodedName}`,
      );
      const link = document.createElement("a");
      link.href = downloadUrl.toString();
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      dispatch(
        showToastNotification({
          content: t("Export download initiated"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showToastNotification({
          content: t("An error occurred while downloading the export"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Sentry.captureException(error);
    }
  }, []);

  const fetchOngoingData = useCallback(async () => {
    try {
      const response = await axiosWrapper.post<GetAllExportDataResponse>(
        `${getBackendUrl()}/schedule/getAllExportData`,
        {
          value: {
            skip: 0,
            take: 100,
            statusFilter: [fileConversionStatus.IN_PROGRESS, fileConversionStatus.ERRORED],
          },
        },
      );
      if (!response.data.status) {
        setOngoingData([]);
        return 0;
      }
      const data = response.data.result;
      const sortedData = [...data].sort((a, b) => b.createdAt - a.createdAt);
      setOngoingData(sortedData);
      return response.data.count || 0;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      return 0;
    }
  }, []);

  const fetchCompletedData = useCallback(
    async (isNextPage = false) => {
      if (isFetchingRef.current || (isNextPage && !hasMore)) return 0;
      isFetchingRef.current = true;
      setIsFetching(true);
      try {
        const currentSkip = isNextPage ? skipRef.current : 0;
        const currentTake = isNextPage ? 10 : skipRef.current || 10;
        const response = await axiosWrapper.post<GetAllExportDataResponse>(
          `${getBackendUrl()}/schedule/getAllExportData`,
          {
            value: {
              skip: currentSkip,
              take: currentTake,
              statusFilter: [fileConversionStatus.COMPLETED, fileConversionStatus.DOWNLOADED],
            },
          },
        );
        const data = response.data.status ? response.data.result : [];
        const apiTotalCount = response.data.status ? response.data.count : undefined;
        const sortedData = [...data].sort((a, b) => b.createdAt - a.createdAt);

        if (isNextPage) {
          setCompletedData((prev) => [...prev, ...sortedData]);
          skipRef.current += 10;
        } else {
          setCompletedData(sortedData);
          if (skipRef.current === 0) {
            skipRef.current = 10;
          }
        }
        setHasMore(data.length === currentTake);
        return apiTotalCount;
      } catch (error) {
        console.error(error);
        return 0;
      } finally {
        isFetchingRef.current = false;
        setIsFetching(false);
      }
    },
    [hasMore],
  );

  // ⚠️ `onCountChange` is in these deps, and `fetchData` is the sole dep of the 30s polling effect
  // below, so an unstable `onCountChange` costs far more than a re-subscribe: a caller written as
  // `<ExportedFiles onCountChange={(c) => setCount(c)} />` gets a new arrow on EVERY parent render ->
  // new `fetchData` -> the poll effect tears down (`clearInterval`) and immediately re-runs
  // `void fetchData()` -> `setLoading(true)` blanks the whole list to the full-page spinner and two
  // `getAllExportData` XHRs fire. A parent re-rendering on any unrelated redux write turns into a
  // fetch storm. The current sole caller (`Notification/index.tsx`) passes a `useCallback` with `[]`
  // deps, which is the only reason this is latent. FIX: mirror `onCountChange` in a ref for identity
  // stability (call `ref.current?.(...)`) and drop it from these deps.
  const fetchData = useCallback(
    async (isNextPage = false) => {
      if (!isNextPage) {
        // BUG: the 30s poll also lands here, so the whole list is replaced by the full-page
        // spinner every 30 seconds.
        setLoading(true);
      }
      try {
        const [ongoingCount, completedCount] = await Promise.all([fetchOngoingData(), fetchCompletedData(isNextPage)]);
        onCountChange?.((ongoingCount || 0) + (completedCount || 0));
      } finally {
        setLoading(false);
      }
    },
    [fetchOngoingData, fetchCompletedData, onCountChange],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting && hasMore && !loading && !isFetchingRef.current) {
          // Fire-and-forget: the IntersectionObserver callback is void-returning.
          void fetchCompletedData(true);
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, loading, fetchCompletedData]);

  // Fire-and-forget polling: useEffect and setInterval callbacks are both void-returning.
  useEffect(() => {
    void fetchData();
    const interval = setInterval(() => {
      void fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchData]);

  const hasNoExports = !loading && ongoingData.length === 0 && completedData.length === 0;

  return (
    <div className="px-6 pt-3 w-full h-full">
      <div className="flex flex-col gap-4 w-full pb-3 h-full">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <IconComponent name="loader-icon" className="animate-spin" width="2rem" height="2rem" />
          </div>
        ) : hasNoExports ? (
          <EmptyExportsState />
        ) : (
          <>
            {ongoingData.length > 0 && (
              <ExportSection
                title="Ongoing"
                items={ongoingData}
                sectionType="ongoing"
                onDelete={handleDelete}
                onRetry={handleRetry}
                onDownload={handleDownload}
              />
            )}
            {completedData.length > 0 && (
              <ExportSection
                title="Completed"
                items={completedData}
                sectionType="completed"
                onDelete={handleDelete}
                onRetry={handleRetry}
                onDownload={handleDownload}
              />
            )}
          </>
        )}
        {hasMore && !loading && <div ref={observerRef} className="h-4 w-full" />}
        {isFetching && (
          <div className="flex items-center justify-center py-4">
            <IconComponent name="loader-icon" className="animate-spin" width="1.5rem" height="1.5rem" />
          </div>
        )}
      </div>
    </div>
  );
}

export default ExportedFiles;
